import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, ShieldCheck, ArrowRight, Gift, Ticket, X, CreditCard, Coins, FlaskConical, PackageCheck } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart, shippingFeeFor } from "../context/CartContext";
import { Seo } from "../components/Seo";
import { EtransferSteps } from "../components/EtransferSteps";
import { ShippingAddressForm, validateShippingAddress, EMPTY_ADDRESS } from "../components/ShippingAddressForm";
import { ShippingMethodPicker, shippingMethodLabel } from "../components/ShippingMethodPicker";
import { openMintagePayPopup, buyerFromOrder } from "../lib/mintagePay";
import visaMark from "../assets/pay-marks/visa.svg";
import mastercardMark from "../assets/pay-marks/mastercard.svg";
import usdcMark from "../assets/pay-marks/usdc.svg";
import usdtMark from "../assets/pay-marks/usdt.svg";
import walletMark from "../assets/pay-marks/wallet.svg";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const TURNSTILE_OPTIONS = { theme: "light" };

const loadSavedAddress = () => {
  try {
    return { ...EMPTY_ADDRESS, ...JSON.parse(localStorage.getItem("shipping_address") || "{}") };
  } catch {
    return { ...EMPTY_ADDRESS };
  }
};

// Card first and selected (Mintage Pay house standard), then crypto, then e-Transfer.
const PAY_MARKS = {
  card: [{ src: visaMark, alt: "Visa" }, { src: mastercardMark, alt: "Mastercard" }],
  crypto: [{ src: usdcMark, alt: "USDC" }, { src: usdtMark, alt: "USDT" }, { src: walletMark, alt: "Crypto wallet" }],
  etransfer: [],
};
const ONLINE_METHODS = ["card", "crypto"];

function PaymentMethodPicker({ value, onChange, feePercent, feeFixed }) {
  const options = [
    { id: "card", label: "Pay by Debit Card", note: `Debit card, in a secure window on this page. Credit cards will not work. +${feePercent}% + $${feeFixed.toFixed(2)} processing fee.` },
    { id: "crypto", label: "Pay with crypto", note: "USDC or USDT from your crypto wallet or exchange, in a secure window on this page." },
    { id: "etransfer", label: "Interac e-Transfer", note: "No fees · verified manually" },
  ];
  return (
    <div role="radiogroup" aria-label="Payment method" className="grid grid-cols-1 gap-3 mb-8" data-testid="payment-method-picker">
      {options.map(({ id, label, note }) => (
        <button
          key={id}
          type="button"
          role="radio"
          aria-checked={value === id}
          data-testid={`payment-method-${id}`}
          onClick={() => onChange(id)}
          className={`text-left border-2 rounded-lg p-4 transition-colors ${
            value === id ? "border-[#8C7B6B] bg-white" : "border-[#DDD7CF] bg-white/60 hover:border-[#8C7B6B]/50"
          }`}
        >
          <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className={`w-4 h-4 shrink-0 rounded-full border-2 ${value === id ? "border-[#8C7B6B] bg-[#8C7B6B]" : "border-[#DDD7CF]"}`} />
            <span className="text-sm font-semibold text-[#1A1A1A]">{label}</span>
            {id === "etransfer" ? (
              <Mail className={`w-5 h-5 ${value === id ? "text-[#8C7B6B]" : "text-[#6E675F]"}`} />
            ) : (
              <span className="flex flex-wrap items-center gap-2">
                {PAY_MARKS[id].map((m) => (
                  <img key={m.alt} src={m.src} alt={m.alt} title={m.alt} width={38} height={24} decoding="async" className="block h-6 w-auto" />
                ))}
              </span>
            )}
          </span>
          <span className="block text-xs text-[#6E675F] mt-1.5 pl-7">{note}</span>
        </button>
      ))}
    </div>
  );
}

export default function Checkout() {
  const { user, loading: authLoading } = useAuth();
  const { items, subtotal, discountAmount, shipCfg, units, clearCart } = useCart();
  const [settings, setSettings] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [address, setAddress] = useState(loadSavedAddress);
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [redeem, setRedeem] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupons, setCoupons] = useState([]);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("etransfer");
  const onlinePay = ONLINE_METHODS.includes(paymentMethod);
  const [protection, setProtection] = useState(false);
  const [researchAck, setResearchAck] = useState(false);
  const mountedAt = useRef(Date.now());
  const turnstileRef = useRef(null);
  const navigate = useNavigate();

  const resetCaptcha = () => {
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  };

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setSettings(data);
      if (data?.card_payments_enabled) setPaymentMethod("card"); // card first and selected
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.name) setAddress((a) => (a.name ? a : { ...a, name: user.name }));
  }, [user]);

  useEffect(() => {
    if (!shipCfg.expressEnabled && shippingMethod === "express") setShippingMethod("standard");
  }, [shipCfg.expressEnabled, shippingMethod]);

  // Auto-apply a remembered affiliate/referral code (?ref=CODE link, 30-day memory)
  const refApplied = useRef(false);
  useEffect(() => {
    if (refApplied.current) return;
    refApplied.current = true;
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem("eb_ref") || "null"); } catch { return; }
    if (!saved?.code || (saved.expires || 0) < Date.now()) return;
    api.post("/coupons/validate", { code: saved.code })
      .then(({ data }) => {
        setCoupons((prev) =>
          prev.some((c) => c.code === data.code) || (!data.stackable && prev.some((c) => !c.stackable))
            ? prev
            : [...prev, data]
        );
        toast.success(`Referral code ${data.code} applied`);
      })
      .catch(() => localStorage.removeItem("eb_ref"));
  }, []);

  // One non-stackable coupon competes with the bulk discount (bigger wins);
  // coupons flagged stackable always apply on top, each on the remaining total.
  const primary = coupons.find((c) => !c.stackable) || null;
  const primaryRaw = primary
    ? +(primary.discount_type === "percent"
        ? (subtotal * primary.discount_value) / 100
        : Math.min(primary.discount_value, subtotal)).toFixed(2)
    : 0;
  const couponWins = !!primary && primaryRaw > discountAmount;
  const bulkApplied = couponWins ? 0 : discountAmount;
  let running = +(subtotal - bulkApplied - (couponWins ? primaryRaw : 0)).toFixed(2);
  const couponRows = couponWins ? [{ code: primary.code, amount: primaryRaw }] : [];
  coupons.filter((c) => c.stackable).forEach((c) => {
    const d = +Math.min(
      c.discount_type === "percent" ? (running * c.discount_value) / 100 : c.discount_value,
      running
    ).toFixed(2);
    if (d > 0) {
      couponRows.push({ code: c.code, amount: d });
      running = +(running - d).toFixed(2);
    }
  });
  const afterDiscounts = running;
  const shipBase = +(subtotal - bulkApplied).toFixed(2);
  const standardDue = shippingFeeFor(shipCfg, "standard", shipBase, units);
  const expressDue = shippingFeeFor(shipCfg, "express", shipBase, units);
  const shippingDue = shippingMethod === "express" ? expressDue : standardDue;
  const redeemRate = settings?.rewards_redeem_rate || 100;
  const rewardsAvailable = !!settings?.rewards_enabled && (user?.points || 0) > 0;
  const rewardsValue = rewardsAvailable ? +Math.min(user.points / redeemRate, afterDiscounts).toFixed(2) : 0;
  const rewardsApplied = redeem && rewardsValue > 0 ? rewardsValue : 0;
  const protectionBase = +(afterDiscounts - rewardsApplied + shippingDue).toFixed(2);
  const protectionFee = protection ? +(protectionBase * 0.02).toFixed(2) : 0;
  const payTotal = +(protectionBase + protectionFee).toFixed(2);
  const feePercent = settings?.card_fee_percent ?? 2.9;
  const feeFixed = settings?.card_fee_fixed ?? 0.3;
  const cardFee = paymentMethod === "card" ? +((payTotal * feePercent) / 100 + feeFixed).toFixed(2) : 0;
  const grandTotal = +(payTotal + cardFee).toFixed(2);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    if (coupons.some((c) => c.code === code)) {
      toast.error("That coupon is already applied");
      return;
    }
    setApplyingCoupon(true);
    try {
      const { data } = await api.post("/coupons/validate", { code });
      const existingPrimary = coupons.find((c) => !c.stackable);
      if (!data.stackable && existingPrimary) {
        toast.error(`${data.code} can't be combined with ${existingPrimary.code} — only one non-stackable coupon per order`);
        return;
      }
      setCoupons((prev) => [...prev, data]);
      setCouponInput("");
      toast.success(`Coupon ${data.code} applied`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid coupon code");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = (code) => setCoupons((prev) => prev.filter((c) => c.code !== code));

  const placeOrder = async () => {
    const addressError = validateShippingAddress(address);
    if (addressError) {
      toast.error(addressError);
      return;
    }
    if (!researchAck) {
      toast.error("Please confirm the research-use acknowledgment before placing your order.");
      return;
    }
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      toast.error("Please complete the human verification.");
      return;
    }
    setPlacing(true);
    try {
      const shippingAddress = { ...address, postal_code: address.postal_code.trim().toUpperCase() };
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping_address: shippingAddress,
        redeem_points: redeem,
        coupon_codes: coupons.map((c) => c.code),
        package_protection: protection,
        research_ack: researchAck,
        payment_method: paymentMethod,
        shipping_method: shippingMethod,
        turnstile_token: captchaToken,
        elapsed_ms: Date.now() - mountedAt.current,
      });
      localStorage.setItem("shipping_address", JSON.stringify(shippingAddress));
      clearCart();
      navigate(`/order/${data.id}`);
      if (onlinePay) {
        try {
          const { data: pay } = await api.post("/payments/checkout", {
            order_id: data.id,
            origin_url: window.location.origin,
          });
          openMintagePayPopup(pay.checkout_url, pay.return_path, buyerFromOrder(data, user.email));
        } catch {
          toast.error("Could not start the payment — you can retry from your order page.");
        }
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      resetCaptcha(); // Turnstile tokens are single-use; issue a fresh one for the retry
      if (typeof detail === "string" && /verification/i.test(detail)) {
        toast.error("Security check didn't go through", {
          description: "It's been reset — please complete it again and retry, or refresh the page.",
          duration: 10000,
          action: { label: "Refresh page", onClick: () => window.location.reload() },
        });
      } else {
        toast.error(detail || "Failed to place order");
      }
    } finally {
      setPlacing(false);
    }
  };

  if (authLoading) {
    return (
      <div className="relative z-10 flex justify-center py-40">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="relative z-10 max-w-md mx-auto px-6 py-32 text-center">
        <ShieldCheck className="w-12 h-12 text-[#8C7B6B] mx-auto mb-6" />
        <h1 className="font-display text-xl sm:text-2xl uppercase tracking-wide text-[#1A1A1A]">Sign in to checkout</h1>
        <p className="text-base text-[#6E675F] mt-4">
          Your account email is tied to your order and payment, so we need to know who you are.
        </p>
        <button
          data-testid="checkout-signin-button"
          onClick={() => navigate("/login?next=/checkout")}
          className="mt-8 bg-[#8C7B6B] text-[#1A1A1A] text-xs tracking-[0.15em] uppercase font-semibold px-10 py-4 rounded-none hover:bg-[#75665A] transition-colors"
        >
          Sign In / Create Account
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="relative z-10 max-w-md mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-xl sm:text-2xl uppercase tracking-wide text-[#1A1A1A]">Your cart is empty</h1>
        <button data-testid="checkout-back-to-shop" onClick={() => navigate("/")} className="mt-8 text-xs tracking-[0.2em] uppercase font-semibold text-[#8C7B6B]">
          Back to shop
        </button>
      </div>
    );
  }

  return (
    <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 py-16">
      <Seo title="Checkout" path="/checkout" noindex />
      <h1 className="font-display text-lg uppercase tracking-wide sm:text-3xl uppercase tracking-wide text-[#1A1A1A] mb-12">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3">
          <ShippingAddressForm address={address} onChange={setAddress} />

          <ShippingMethodPicker value={shippingMethod} onChange={setShippingMethod} cfg={shipCfg} standardDue={standardDue} expressDue={expressDue} />

          {settings?.card_payments_enabled && (
            <PaymentMethodPicker value={paymentMethod} onChange={setPaymentMethod} feePercent={feePercent} feeFixed={feeFixed} />
          )}

          {!onlinePay || !settings?.card_payments_enabled ? (
            <div className="border-2 border-[#8C7B6B] rounded-lg p-8 bg-white" data-testid="checkout-etransfer-instructions">
              <div className="flex items-center gap-3 mb-6">
                <Mail className="w-6 h-6 text-[#8C7B6B]" />
                <h2 className="font-display text-base sm:text-xl uppercase tracking-wide text-[#1A1A1A]">Payment by Interac e-Transfer</h2>
              </div>
              <EtransferSteps
                testidPrefix="checkout"
                steps={settings?.etransfer_steps || []}
                values={{
                  AMOUNT: { text: `$${payTotal.toFixed(2)} CAD`, testid: "checkout-amount", className: "text-[#8C7B6B]" },
                  ETRANSFER_EMAIL: { text: settings?.etransfer_email || "…", testid: "checkout-etransfer-email" },
                  RECIPIENT_NAME: { text: settings?.etransfer_name || "", testid: "checkout-etransfer-name" },
                  YOUR_EMAIL: { text: user.email, testid: "checkout-user-email" },
                  ORDER_NUMBER: { text: "(shown after you place the order)", plain: true },
                }}
              />
              {settings?.instructions && (
                <p className="text-sm text-[#6E675F] mt-6 pt-6 border-t border-[#DDD7CF] whitespace-pre-line" data-testid="checkout-custom-instructions">
                  {settings.instructions}
                </p>
              )}
            </div>
          ) : paymentMethod === "card" ? (
            <div className="border-2 border-[#8C7B6B] rounded-lg p-8 bg-white" data-testid="checkout-card-info">
              <div className="flex items-center gap-3 mb-5">
                <CreditCard className="w-6 h-6 text-[#8C7B6B]" />
                <h2 className="font-display text-lg sm:text-xl uppercase tracking-wide text-[#1A1A1A]">Pay by Debit Card</h2>
              </div>
              <p className="text-sm text-[#6E675F] leading-relaxed">
                After placing your order, a <strong className="text-[#1A1A1A]">secure payment window</strong> opens on this page for your
                debit card. <strong className="text-[#1A1A1A]">Credit cards will not work.</strong> Your order is confirmed automatically
                the moment payment succeeds — no waiting for manual verification.
              </p>
              <p className="text-sm text-[#6E675F] leading-relaxed mt-4" data-testid="checkout-card-statement-note">
                Your card statement will show this charge as STRIPE*PRIVY, INC. (our secure card processor), not as Eternal Biolabs.
                The amount is your order total.
              </p>
              <p className="text-sm text-[#6E675F] leading-relaxed mt-4" data-testid="checkout-card-fee-note">
                A card processing fee of <strong className="text-[#1A1A1A]">{feePercent}% + ${feeFixed.toFixed(2)}</strong> is
                added to card payments{cardFee > 0 ? <> (<strong className="text-[#1A1A1A]">${cardFee.toFixed(2)}</strong> on this order)</> : null}.
                Prefer no fee? Choose Interac e-Transfer.
              </p>
            </div>
          ) : (
            <div className="border-2 border-[#8C7B6B] rounded-lg p-8 bg-white" data-testid="checkout-crypto-info">
              <div className="flex items-center gap-3 mb-5">
                <Coins className="w-6 h-6 text-[#8C7B6B]" />
                <h2 className="font-display text-lg sm:text-xl uppercase tracking-wide text-[#1A1A1A]">Pay with Crypto</h2>
              </div>
              <p className="text-sm text-[#6E675F] leading-relaxed">
                After placing your order, a <strong className="text-[#1A1A1A]">secure payment window</strong> opens on this page. Pay in
                USDC or USDT from your crypto wallet or exchange — the window shows the exact amount and network. Your order is
                confirmed automatically once the payment arrives.
              </p>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="border border-[#DDD7CF] rounded-lg p-6 bg-white">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-5">Order Summary</p>
            <div className="space-y-3">
              {items.map((i) => (
                <div key={i.product_id} className="flex justify-between text-sm">
                  <span className="text-[#1A1A1A]" data-testid={`checkout-summary-item-${i.product_id}`}>
                    {i.name}{i.dosage ? ` ${i.dosage}` : ""} × {i.quantity}
                  </span>
                  <span className="text-[#6E675F]">${(i.price * i.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 space-y-2" data-testid="coupon-box">
              {coupons.map((c) => {
                const inactive = !c.stackable && !couponWins;
                return (
                  <div key={c.code} className={`flex items-center justify-between rounded-lg px-4 py-3 border ${inactive ? "bg-[#FEF3E2] border-[#B45309]/30" : "bg-[#EAF2EC] border-[#2D6A4F]/30"}`}>
                    <span className="text-sm text-[#1A1A1A] flex items-center gap-2 min-w-0">
                      <Ticket className={`w-4 h-4 shrink-0 ${inactive ? "text-[#B45309]" : "text-[#2D6A4F]"}`} />
                      <strong data-testid={`applied-coupon-code-${c.code}`}>{c.code}</strong>
                      <span className={`truncate ${inactive ? "text-[#B45309]" : "text-[#2D6A4F]"}`}>
                        ({c.discount_type === "percent" ? `${c.discount_value}% off` : `$${c.discount_value.toFixed(2)} off`}{c.stackable ? " · stackable" : ""})
                      </span>
                    </span>
                    <button data-testid={`remove-coupon-button-${c.code}`} onClick={() => removeCoupon(c.code)} className="text-[#6E675F] hover:text-[#1A1A1A] shrink-0" aria-label={`Remove coupon ${c.code}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
              {primary && !couponWins && (
                <p className="text-xs text-[#B45309]" data-testid="coupon-not-applied-note">
                  {primary.code} not applied — your bulk discount (−${discountAmount.toFixed(2)}) is bigger. Non-stackable coupons don't combine; the larger discount applies.
                </p>
              )}
              {couponWins && discountAmount > 0 && (
                <p className="text-xs text-[#6E675F]" data-testid="coupon-replaces-bulk-note">
                  {primary.code} replaces your bulk discount (−${discountAmount.toFixed(2)}).
                </p>
              )}
              <div className="flex gap-2">
                <input
                  data-testid="coupon-code-input"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                  placeholder={coupons.length ? "Add another coupon" : "Coupon code"}
                  className="flex-1 min-w-0 border border-[#DDD7CF] rounded-lg px-4 py-2.5 text-sm uppercase tracking-wide focus:outline-none focus:border-[#8C7B6B] bg-white"
                />
                <button
                  data-testid="apply-coupon-button"
                  onClick={applyCoupon}
                  disabled={applyingCoupon || !couponInput.trim()}
                  className="text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B] border border-[#8C7B6B] rounded-lg px-4 hover:bg-[#8C7B6B] hover:text-white transition-colors disabled:opacity-50"
                >
                  {applyingCoupon ? "…" : "Apply"}
                </button>
              </div>
            </div>

            {rewardsAvailable && rewardsValue > 0 && (
              <label className="mt-5 flex items-start gap-3 bg-[#F6F1E7] border border-[#D4B36A]/50 rounded-lg px-4 py-3 cursor-pointer" data-testid="rewards-redeem-box">
                <input
                  type="checkbox"
                  checked={redeem}
                  onChange={(e) => setRedeem(e.target.checked)}
                  data-testid="redeem-points-checkbox"
                  className="mt-0.5 w-5 h-5 shrink-0 accent-[#8C7B6B]"
                />
                <span className="text-sm text-[#1A1A1A]">
                  <Gift className="w-3.5 h-3.5 inline mr-1 text-[#8C7B6B]" />
                  Apply my <strong>{user.points}</strong> reward points — save <strong>${rewardsValue.toFixed(2)}</strong>
                </span>
              </label>
            )}

            <label className="mt-5 flex items-start gap-3 bg-[#EEF2F0] border border-[#2D6A4F]/25 rounded-lg px-4 py-3 cursor-pointer" data-testid="package-protection-box">
              <input
                type="checkbox"
                checked={protection}
                onChange={(e) => setProtection(e.target.checked)}
                data-testid="package-protection-checkbox"
                className="mt-0.5 w-5 h-5 shrink-0 accent-[#8C7B6B]"
              />
              <span className="text-sm text-[#1A1A1A]">
                <PackageCheck className="w-3.5 h-3.5 inline mr-1 text-[#2D6A4F]" />
                <strong>Package Protection</strong> — signature required on delivery so no porch pirate walks off with your order (+2%
                {protection && protectionFee > 0 ? <>, <strong>${protectionFee.toFixed(2)}</strong></> : null})
              </span>
            </label>

            <div className="border-t border-[#DDD7CF] mt-5 pt-5 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#6E675F]">Subtotal</span>
                <span className="text-[#1A1A1A]" data-testid="checkout-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              {bulkApplied > 0 && (
                <div className="flex justify-between text-sm" data-testid="checkout-discount-row">
                  <span className="text-[#2D6A4F] font-medium">Bulk discounts</span>
                  <span className="text-[#2D6A4F] font-medium" data-testid="checkout-discount-amount">−${bulkApplied.toFixed(2)}</span>
                </div>
              )}
              {couponRows.map((r) => (
                <div key={r.code} className="flex justify-between text-sm" data-testid={`checkout-coupon-row-${r.code}`}>
                  <span className="text-[#2D6A4F] font-medium">Coupon ({r.code})</span>
                  <span className="text-[#2D6A4F] font-medium" data-testid={`checkout-coupon-amount-${r.code}`}>−${r.amount.toFixed(2)}</span>
                </div>
              ))}
              {rewardsApplied > 0 && (
                <div className="flex justify-between text-sm" data-testid="checkout-rewards-row">
                  <span className="text-[#8A6D2F] font-medium">Rewards</span>
                  <span className="text-[#8A6D2F] font-medium" data-testid="checkout-rewards-amount">−${rewardsApplied.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm" data-testid="checkout-shipping-row">
                <span className="text-[#6E675F]">Shipping <span data-testid="checkout-shipping-method-label">({shippingMethodLabel(shippingMethod)})</span></span>
                {shippingDue > 0 ? (
                  <span className="text-[#1A1A1A]" data-testid="checkout-shipping-amount">${shippingDue.toFixed(2)}</span>
                ) : (
                  <span className="text-[#2D6A4F] font-medium" data-testid="checkout-shipping-amount">FREE</span>
                )}
              </div>
              {protectionFee > 0 && (
                <div className="flex justify-between text-sm" data-testid="checkout-protection-row">
                  <span className="text-[#6E675F]">Package Protection (+2%)</span>
                  <span className="text-[#1A1A1A]" data-testid="checkout-protection-amount">${protectionFee.toFixed(2)}</span>
                </div>
              )}
              {cardFee > 0 && (
                <div className="flex justify-between text-sm" data-testid="checkout-card-fee-row">
                  <span className="text-[#6E675F]">Card processing fee</span>
                  <span className="text-[#1A1A1A]" data-testid="checkout-card-fee-amount">${cardFee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-[#DDD7CF]">
                <span className="text-sm font-semibold text-[#1A1A1A]">Total</span>
                <span className="text-2xl font-medium text-[#1A1A1A]" data-testid="checkout-total">${grandTotal.toFixed(2)} CAD</span>
              </div>
            </div>

            <label className="mt-5 flex items-start gap-3 bg-[#F7F5F2] border border-[#DDD7CF] rounded-lg px-4 py-3 cursor-pointer" data-testid="research-ack-box">
              <input
                type="checkbox"
                checked={researchAck}
                onChange={(e) => setResearchAck(e.target.checked)}
                data-testid="research-ack-checkbox"
                className="mt-0.5 w-5 h-5 shrink-0 accent-[#8C7B6B]"
              />
              <span className="text-xs text-[#6E675F] leading-relaxed">
                <FlaskConical className="w-3.5 h-3.5 inline mr-1 text-[#8C7B6B]" />
                I confirm I am a qualified researcher and these products are purchased strictly for
                <strong className="text-[#1A1A1A]"> laboratory research purposes only</strong>. They are not for human or
                veterinary use, diagnosis, or treatment of any kind outside of approved clinical studies. *
              </span>
            </label>

            {TURNSTILE_SITE_KEY && (
              <div className="mt-5 flex justify-center" data-testid="checkout-turnstile">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  options={TURNSTILE_OPTIONS}
                  onSuccess={(token) => setCaptchaToken(token)}
                  onExpire={() => setCaptchaToken(null)}
                  onError={() => setCaptchaToken(null)}
                />
              </div>
            )}

            <button
              data-testid="place-order-button"
              onClick={placeOrder}
              disabled={placing || !researchAck || (TURNSTILE_SITE_KEY && !captchaToken)}
              className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-8 py-4 rounded-none hover:bg-[#75665A] transition-colors disabled:opacity-60"
            >
              {placing ? "Placing Order…" : paymentMethod === "card" ? <>Place Order & Pay by Card <ArrowRight className="w-4 h-4" /></> : paymentMethod === "crypto" ? <>Place Order & Pay with Crypto <ArrowRight className="w-4 h-4" /></> : <>Place Order <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-xs text-[#6E675F] mt-4 text-center flex items-center justify-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              {onlinePay
                ? "Protected checkout · secure payment window on this page."
                : "Protected checkout · order stays pending until your e-Transfer is received."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
